import React from 'react';
import { useSelector } from 'react-redux';
import { getOrLoadGroupDetails, groupIsMember } from 'modules/contacts/stores/contacts-slice-handler';
import Preloader from 'modules/shared/components/preloader';
import classNames from 'classnames';
import Tooltip from '@mui/material/Tooltip';
import ContactMenu from './contact-menu';
import { ContactMenuType } from '../../../constants/contacts-constants';
import LabelWithCheckbox from '../../shared/components/label-with-checkbox';

/**
 * Render the details of a group asynchronously.
 * @param {string} groupId
 * @param {boolean} withFavoriteControls If should render the favorite controls
 * @param {ContactMenuType} menuType If should render a menu or not. (optional)
 * @param {boolean} multiSelectActive If the multi-select mode is enabled or not. When enabled a checkbox is shown. (optional)
 * @param {boolean} multiSelectState The initial state of the multi-selection checkbox. (optional)
 * @param {boolean} multiSelectDisableState If the checkbox is enabled or not. (optional)
 * @param {Function} multiSelectOnChange A callback to be executed when the checkbox changes. (optional)
 * @param {Function} nameClickCallback A callback to be executed when the name of the contact is clicked. (optional)
 *                                     The callback will be called only if not multi-select mode is enabled.
 *                                     The callback will receive as argument the current contact.
 * @return {JSX.Element}
 * @constructor
 */
export default function ContactGroupItem({
    groupId,
    withFavoriteControls,
    menuType,
    multiSelectActive,
    multiSelectState,
    multiSelectDisableState,
    multiSelectOnChange,
    nameClickCallback,
    ...otherProps
}) {
    const group = useSelector(() => getOrLoadGroupDetails(groupId));
    const isMember = useSelector(() => groupIsMember(groupId));

    if (group === null) {
        return '';
    }

    const onNameClick = () => {
        if (multiSelectActive) {
            return;
        }

        if (nameClickCallback) {
            nameClickCallback(group);
        }
    };

    return (
        <div
            className={classNames({
                'wcf-ui-contact-item': true,
                'contact-group-item': true,
                'with-membership': isMember,
                'with-checkbox': !!multiSelectActive,
                'with-favorite-star': withFavoriteControls === true && group.IsFavorite,
                disabled: !!multiSelectActive && !!multiSelectDisableState,
            })}
            data-test-id={`contact-item-group-${group.name}`}
            {...otherProps}
        >
            {(group === null || group.IsUpdating) && <Preloader asOverlay />}
            {multiSelectActive && (
                <div className={'wcf-ui-checkbox-container'} data-test-id={`checkbox-${group.name}`}>
                    <LabelWithCheckbox
                        labelContent={<div className={'multi-select-area'}></div>}
                        onChange={(event) => {
                            multiSelectOnChange(group, event.target.checked);
                        }}
                        checked={!!multiSelectState}
                        disabled={!!multiSelectDisableState}
                        variation={'round'}
                    ></LabelWithCheckbox>
                </div>
            )}
            <div className={'group-icon-container'}>
                <span className={'stw-group'}></span>
            </div>
            {group.ExternalLinkChannel && (
                <div className={'external-link-container'} data-test-id={'external-link-icon'}>
                    <span className={'stw-link'}></span>
                </div>
            )}
            <div className={'group-name-container'}>
                <Tooltip title={group.Name} arrow>
                    <div className={'group-name'} onClick={() => onNameClick()}>
                        {group.Name}
                    </div>
                </Tooltip>
            </div>
            {withFavoriteControls === true && group.IsFavorite && (
                <div className={'favorite-controls-container'} data-test-id={'favorite-controls-icon'}>
                    <span className={'stw-favorite-active'}></span>
                </div>
            )}
            {!multiSelectActive && menuType && <ContactMenu contact={group} menuType={menuType} />}
            <div>{otherProps.children}</div>
        </div>
    );
}
